import React, { useState, useEffect } from 'react';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from '@polichat/flamboyant';
import { Icon } from '@polichat/flamboyant';
import { useGlotaLocale, useGlotaT } from 'src/hooks/useGlotaTranslation';
import { UserPreferencesService } from '../../../../../services/user-preferences.service';

const LanguageSelector = () => {
  const { currentLocale, changeLanguage, isLoading } = useGlotaLocale();
  const languageText = useGlotaT('spa.common.language');
  const portugueseText = useGlotaT('spa.common.portuguese');
  const spanishText = useGlotaT('spa.common.spanish');
  
  // Garante que sempre temos um locale válido (fallback para pt-BR)
  const displayLocale = currentLocale || 'pt-BR';
  
  // Detecta se o tema escuro está ativo (polichat-spa usa classe 'darkMode' no body)
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  
  useEffect(() => {
    const checkTheme = () => {
      const body = document.body || document.documentElement;
      // Verifica tanto data-theme quanto a classe darkMode (polichat-spa usa darkMode)
      const hasDarkMode = body.classList.contains('darkMode') || body.getAttribute('data-theme') === 'dark';
      setIsDarkTheme(hasDarkMode);
    };
    
    checkTheme();
    
    // Observa mudanças nas classes e no atributo data-theme
    const observer = new MutationObserver(checkTheme);
    const target = document.body || document.documentElement;
    observer.observe(target, {
      attributes: true,
      attributeFilter: ['data-theme', 'class']
    });
    
    // Também observa mudanças de classe diretamente
    const bodyObserver = new MutationObserver(checkTheme);
    if (document.body) {
      bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    return () => {
      observer.disconnect();
      bodyObserver.disconnect();
    };
  }, []);

  const handleLanguageChange = async (newLocale) => {
    if (newLocale === currentLocale || isLoading) return;

    try {
      // Salva no backend
      await UserPreferencesService.updateLanguage(newLocale);
      
      // Atualiza o contexto Glota
      await changeLanguage(newLocale);
      
      // Salva no localStorage para persistência
      try {
        localStorage.setItem('lg', newLocale.split('-')[0]); // Salva como 'pt' ou 'es'
      } catch (e) {
        console.warn('Não foi possível salvar idioma no localStorage:', e);
      }
      
      // Recarrega a página para garantir que todas as traduções sejam atualizadas
      window.location.reload();
    } catch (error) {
      console.error('Erro ao alterar idioma:', error);
    }
  };

  const getLanguageName = (locale) => {
    // Fallback direto para textos hardcoded caso traduções não estejam disponíveis
    const ptText = (portugueseText && portugueseText !== 'spa.common.portuguese' && portugueseText !== '') 
      ? portugueseText 
      : 'Português';
    const esText = (spanishText && spanishText !== 'spa.common.spanish' && spanishText !== '') 
      ? spanishText 
      : 'Español';
    
    // Determina qual texto mostrar baseado no locale
    if (!locale || locale === 'pt-BR' || locale === 'pt') {
      return ptText;
    }
    if (locale === 'es-ES' || locale === 'es') {
      return esText;
    }
    // Fallback final
    return ptText || 'Português';
  };

  return (
    <div className="languageSelector">
      <style>{`
        .languageContainer {
          background-color: #fff !important;
          color: #000 !important;
          z-index: 1000 !important;
          border: 1px solid #ddd !important;
          border-radius: 4px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
        }
        .languageOption {
          color: #000 !important;
          background-color: transparent !important;
        }
        .languageOption:hover {
          background-color: #f0f0f0 !important;
          color: #000 !important;
        }
        .languageOption.active {
          background-color: #f0f0f0 !important;
          color: #000 !important;
        }
        .languageOption span {
          color: #000 !important;
        }
        .languageOption div {
          color: #000 !important;
        }
        /* Garante visibilidade quando sidebar está recolhida */
        .menuRetratil .languageSelector .languageContainer {
          position: absolute !important;
          left: 0 !important;
          min-width: 200px !important;
        }
        /* Garante que o texto sempre apareça */
        .languageSelector .nameLanguage {
          display: inline-block !important;
          visibility: visible !important;
          opacity: 1 !important;
          color: inherit !important;
        }
        .menuRetratil .languageSelector .nameLanguage {
          display: inline-block !important;
        }
        /* Estilos para tema escuro - polichat-spa usa classe darkMode no body */
        body.darkMode .languageSelector .languageContainer,
        body.darkMode .change-language .languageContainer,
        body.darkMode .languageContainer.languageContainer,
        [data-theme='dark'] .languageSelector .languageContainer,
        [data-theme='dark'] .change-language .languageContainer,
        [data-theme='dark'] .languageContainer.languageContainer {
          background-color: #12121B !important;
          color: #F5F6F8 !important;
          border-color: #232C3D !important;
        }
        body.darkMode .languageSelector .languageOption,
        body.darkMode .change-language .languageOption,
        body.darkMode .languageContainer .languageOption,
        [data-theme='dark'] .languageSelector .languageOption,
        [data-theme='dark'] .change-language .languageOption,
        [data-theme='dark'] .languageContainer .languageOption {
          color: #fff !important;
          background-color: transparent !important;
        }
        body.darkMode .languageSelector .languageOption:hover,
        body.darkMode .change-language .languageOption:hover,
        body.darkMode .languageContainer .languageOption:hover,
        [data-theme='dark'] .languageSelector .languageOption:hover,
        [data-theme='dark'] .change-language .languageOption:hover,
        [data-theme='dark'] .languageContainer .languageOption:hover {
          background-color: #fff !important;
          color: #000 !important;
        }
        body.darkMode .languageSelector .languageOption:hover span,
        body.darkMode .change-language .languageOption:hover span,
        body.darkMode .languageContainer .languageOption:hover span,
        [data-theme='dark'] .languageSelector .languageOption:hover span,
        [data-theme='dark'] .change-language .languageOption:hover span,
        [data-theme='dark'] .languageContainer .languageOption:hover span {
          color: #000 !important;
        }
        body.darkMode .languageSelector .languageOption:hover div,
        body.darkMode .change-language .languageOption:hover div,
        body.darkMode .languageContainer .languageOption:hover div,
        [data-theme='dark'] .languageSelector .languageOption:hover div,
        [data-theme='dark'] .change-language .languageOption:hover div,
        [data-theme='dark'] .languageContainer .languageOption:hover div {
          color: #000 !important;
        }
        body.darkMode .languageSelector .languageOption.active,
        body.darkMode .change-language .languageOption.active,
        body.darkMode .languageContainer .languageOption.active,
        [data-theme='dark'] .languageSelector .languageOption.active,
        [data-theme='dark'] .change-language .languageOption.active,
        [data-theme='dark'] .languageContainer .languageOption.active {
          background-color: rgba(102, 102, 222, 0.2) !important;
          color: #fff !important;
        }
        body.darkMode .languageSelector .languageOption span,
        body.darkMode .change-language .languageOption span,
        body.darkMode .languageContainer .languageOption span,
        [data-theme='dark'] .languageSelector .languageOption span,
        [data-theme='dark'] .change-language .languageOption span,
        [data-theme='dark'] .languageContainer .languageOption span {
          color: #fff !important;
        }
        body.darkMode .languageSelector .languageOption div,
        body.darkMode .change-language .languageOption div,
        body.darkMode .languageContainer .languageOption div,
        [data-theme='dark'] .languageSelector .languageOption div,
        [data-theme='dark'] .change-language .languageOption div,
        [data-theme='dark'] .languageContainer .languageOption div {
          color: #fff !important;
        }
        body.darkMode .languageSelector .change-language button,
        body.darkMode .change-language button,
        [data-theme='dark'] .languageSelector .change-language button,
        [data-theme='dark'] .change-language button {
          color: #F5F6F8 !important;
        }
        body.darkMode .languageSelector .change-language button:hover,
        body.darkMode .change-language button:hover,
        [data-theme='dark'] .languageSelector .change-language button:hover,
        [data-theme='dark'] .change-language button:hover {
          color: #6666de !important;
        }
        body.darkMode .languageSelector .nameLanguage,
        [data-theme='dark'] .languageSelector .nameLanguage {
          color: #F5F6F8 !important;
        }
      `}</style>
      <UncontrolledDropdown className="change-language">
        <DropdownToggle
          color="neutral"
          caret
          id="languageSelector"
          name="languageSelector"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon icon="poli-icon pi-global-line" size={16} />
            <span className="nameLanguage">
              {getLanguageName(displayLocale) || 'Português'}
            </span>
          </div>
        </DropdownToggle>

        <DropdownMenu 
          className="languageContainer" 
          style={{ 
            zIndex: 1000,
            backgroundColor: isDarkTheme ? '#12121B' : '#fff',
            color: isDarkTheme ? '#F5F6F8' : '#000',
            borderColor: isDarkTheme ? '#232C3D' : '#ddd'
          }}
        >
          <DropdownItem
            className="languageOption"
            onClick={() => handleLanguageChange('pt-BR')}
            active={currentLocale === 'pt-BR' || currentLocale === 'pt'}
            style={{
              color: isDarkTheme ? '#fff' : '#000',
              backgroundColor: currentLocale === 'pt-BR' || currentLocale === 'pt' 
                ? (isDarkTheme ? 'rgba(102, 102, 222, 0.2)' : '#f0f0f0')
                : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (isDarkTheme) {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.color = '#000';
                const spans = e.currentTarget.querySelectorAll('span');
                spans.forEach(span => span.style.color = '#000');
              }
            }}
            onMouseLeave={(e) => {
              if (isDarkTheme) {
                const isActive = currentLocale === 'pt-BR' || currentLocale === 'pt';
                e.currentTarget.style.backgroundColor = isActive ? 'rgba(102, 102, 222, 0.2)' : 'transparent';
                e.currentTarget.style.color = '#fff';
                const spans = e.currentTarget.querySelectorAll('span');
                spans.forEach(span => span.style.color = '#fff');
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'inherit' }}>
              <span style={{ color: 'inherit' }}>🇧🇷</span>
              <span style={{ color: 'inherit' }}>{portugueseText}</span>
            </div>
          </DropdownItem>

          <DropdownItem
            className="languageOption"
            onClick={() => handleLanguageChange('es-ES')}
            active={currentLocale === 'es-ES' || currentLocale === 'es'}
            style={{
              color: isDarkTheme ? '#fff' : '#000',
              backgroundColor: currentLocale === 'es-ES' || currentLocale === 'es'
                ? (isDarkTheme ? 'rgba(102, 102, 222, 0.2)' : '#f0f0f0')
                : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (isDarkTheme) {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.color = '#000';
                const spans = e.currentTarget.querySelectorAll('span');
                spans.forEach(span => span.style.color = '#000');
              }
            }}
            onMouseLeave={(e) => {
              if (isDarkTheme) {
                const isActive = currentLocale === 'es-ES' || currentLocale === 'es';
                e.currentTarget.style.backgroundColor = isActive ? 'rgba(102, 102, 222, 0.2)' : 'transparent';
                e.currentTarget.style.color = '#fff';
                const spans = e.currentTarget.querySelectorAll('span');
                spans.forEach(span => span.style.color = '#fff');
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'inherit' }}>
              <span style={{ color: 'inherit' }}>🇪🇸</span>
              <span style={{ color: 'inherit' }}>{spanishText}</span>
            </div>
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

export { LanguageSelector };

